import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { doc, setDoc, updateDoc, onSnapshot, runTransaction, Unsubscribe } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../services/firebase';
import { parsePlayerMismatch, type PlayerMismatch, type QueueNextUp } from '../utils/playerMismatch';
import { getBridgeStatus, type BridgeStatus } from '../utils/bridgeStatus';
import { parseLocalFilePlayer, type LocalFilePlayer } from '../utils/localFileLevels';
import { parseBridgeLaunchAck, type BridgeLaunchAck } from '../utils/bridgeLaunch';
import { parsePlayerPosition, positionAnchorMs, type PlayerPositionReport } from '../utils/nowPlayingCountdown';

const STORAGE_KEY = 'votebeats_active_playback';
const FIRESTORE_FIELD = 'activePlayback';
const STATUS_TICK_MS = 5_000;

/** Generate a stable device ID per browser tab (sessionStorage is tab-scoped). */
function getDeviceId(): string {
  const KEY = 'votebeats_device_id';
  try {
    const cached = sessionStorage.getItem(KEY);
    if (cached) return cached;
  } catch { /* Safari private browsing */ }
  const id = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try { sessionStorage.setItem(KEY, id); } catch { /* Safari private browsing */ }
  return id;
}

export interface ActivePlayback {
  eventId: string;
  eventName: string;
  lockedAt: string;
  deviceId: string;
  heartbeatAt?: string;
  bridgeVersion?: string;
  playerPosition?: PlayerPositionReport;
}

export type PlaybackCommand = 'play' | 'pause' | 'skip' | 'previous';

interface ActivePlaybackContextType {
  activePlayback: ActivePlayback | null;
  lockPlayback: (eventId: string, eventName: string) => void;
  unlockPlayback: () => void;
  isLockedToOtherEvent: (eventId: string) => boolean;
  isActivePoller: boolean;
  /** Player name detected by desktop helper, e.g. "Spotify", "Apple Music" */
  detectedPlayerName: string | null;
  /** Send a playback command to the desktop helper via Firestore */
  sendRemoteCommand: (cmd: PlaybackCommand) => Promise<void>;
  /** Tell the desktop helper to switch to a different event */
  sendSwitchEventCommand: (eventId: string, eventName: string) => Promise<void>;
  /** What the desktop helper saw playing when it couldn't match the song to the queue */
  playerMismatch: PlayerMismatch | null;
  /** Mark the note acknowledged — only if it is still the note identified by detectedAt */
  acknowledgePlayerMismatch: (detectedAt: string) => Promise<void>;
  /** Remove the note for this event (used when the DJ advances by hand) */
  clearPlayerMismatch: (eventId: string) => Promise<void>;
  /** Tell the desktop helper which request the Queue page shows on top */
  publishQueueNextUp: (eventId: string, requestId: string | null) => void;
  /** This browser tab's device ID */
  deviceId: string;
  /** Bridge status for an event, from the lock and its heartbeat (re-evaluated every 5 s) */
  bridgeStatusFor: (eventId: string | undefined) => BridgeStatus;
  /** Which Queue page tab plays local files */
  localFilePlayer: LocalFilePlayer | null;
  /** True once the user doc has been read, so a missing claim really means "no claim" */
  localFilePlayerLoaded: boolean;
  claimLocalFilePlayer: (eventId: string) => Promise<void>;
  releaseLocalFilePlayer: (eventId: string) => Promise<void>;
  beatLocalFilePlayer: (eventId: string) => void;
  /** Bridge's answer to the last launch link it opened */
  bridgeLaunchAck: BridgeLaunchAck | null;
  /** When the current playerPosition was true, in this browser's clock (null without a position) */
  playerPositionAnchorMs: number | null;
}

const ActivePlaybackContext = createContext<ActivePlaybackContextType | undefined>(undefined);

function parseActivePlayback(raw: unknown): ActivePlayback | null {
  if (!raw || typeof raw !== 'object') return null;
  const { playerPosition, bridgeVersion, ...rest } = raw as ActivePlayback & Record<string, unknown>;
  const parsedPosition = parsePlayerPosition(playerPosition);
  return {
    ...rest,
    ...(typeof bridgeVersion === 'string' ? { bridgeVersion } : {}),
    ...(parsedPosition ? { playerPosition: parsedPosition } : {}),
  };
}

function readFromStorage(): ActivePlayback | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

async function writePlaybackToFirestore(
  uid: string,
  playback: ActivePlayback | null
): Promise<void> {
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, { [FIRESTORE_FIELD]: playback }, { merge: true });
}

export function ActivePlaybackProvider({ children }: { children: ReactNode }) {
  const deviceId = useMemo(() => getDeviceId(), []);
  const [activePlayback, setActivePlayback] = useState<ActivePlayback | null>(readFromStorage);
  const [detectedPlayerName, setDetectedPlayerName] = useState<string | null>(null);
  const [playerMismatch, setPlayerMismatch] = useState<PlayerMismatch | null>(null);
  const [localFilePlayer, setLocalFilePlayer] = useState<LocalFilePlayer | null>(null);
  const [localFilePlayerLoaded, setLocalFilePlayerLoaded] = useState(false);
  const localFilePlayerRef = useRef<LocalFilePlayer | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [bridgeLaunchAck, setBridgeLaunchAck] = useState<BridgeLaunchAck | null>(null);
  const [playerPositionAnchorMs, setPlayerPositionAnchorMs] = useState<number | null>(null);
  const lastPositionReportedAtRef = useRef<string | null>(null);

  useEffect(() => {
    localFilePlayerRef.current = localFilePlayer;
  }, [localFilePlayer]);

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), STATUS_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  // Firestore real-time listener — syncs active playback across devices
  useEffect(() => {
    let unsubFirestore: Unsubscribe | null = null;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      // Clean up previous Firestore listener
      if (unsubFirestore) {
        unsubFirestore();
        unsubFirestore = null;
      }

      if (!user) {
        // Not signed in — clear local state
        setActivePlayback(null);
        setLocalFilePlayer(null);
        setLocalFilePlayerLoaded(false);
        setBridgeLaunchAck(null);
        setPlayerPositionAnchorMs(null);
        lastPositionReportedAtRef.current = null;
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      const userRef = doc(db, 'users', user.uid);
      unsubFirestore = onSnapshot(userRef, (snap) => {
        setLocalFilePlayerLoaded(true);
        if (!snap.exists()) return;
        const data = snap.data();
        const firestorePlayback = parseActivePlayback(data[FIRESTORE_FIELD]);

        const reportedAt = firestorePlayback?.playerPosition?.reportedAt ?? null;
        if (reportedAt !== lastPositionReportedAtRef.current) {
          lastPositionReportedAtRef.current = reportedAt;
          setPlayerPositionAnchorMs(reportedAt === null ? null : positionAnchorMs(reportedAt, Date.now()));
        }

        setActivePlayback(firestorePlayback);
        setBridgeLaunchAck(parseBridgeLaunchAck(data['bridgeLaunchAck']));
        setDetectedPlayerName(data['detectedPlayerName'] ?? null);
        setPlayerMismatch(parsePlayerMismatch(data['playerMismatch']));
        setLocalFilePlayer(parseLocalFilePlayer(data['localFilePlayer']));

        // Mirror to localStorage for cross-tab sync within same browser
        if (firestorePlayback) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(firestorePlayback));
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      }, (error) => {
        console.error('ActivePlayback Firestore listener error:', error);
      });
    });

    return () => {
      unsubAuth();
      if (unsubFirestore) unsubFirestore();
    };
  }, []);

  // Cross-tab sync for same browser (other tabs get localStorage writes from onSnapshot handler)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setActivePlayback(e.newValue ? JSON.parse(e.newValue) : null);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const lockPlayback = useCallback((eventId: string, eventName: string) => {
    const lock: ActivePlayback = {
      eventId,
      eventName,
      lockedAt: new Date().toISOString(),
      deviceId,
    };
    // Optimistic: update localStorage + React state immediately
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lock));
    setActivePlayback(lock);
    // Persist to Firestore (fire-and-forget; onSnapshot will reconcile)
    const uid = auth.currentUser?.uid;
    if (uid) {
      writePlaybackToFirestore(uid, lock).catch(console.error);
    }
  }, [deviceId]);

  const unlockPlayback = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setActivePlayback(null);
    const uid = auth.currentUser?.uid;
    if (uid) {
      writePlaybackToFirestore(uid, null).catch(console.error);
    }
  }, []);

  const isLockedToOtherEvent = useCallback((eventId: string) => {
    const current = readFromStorage();
    return getBridgeStatus(current, eventId, Date.now()).kind === 'live-elsewhere';
  }, []);

  const bridgeStatusFor = useCallback(
    (eventId: string | undefined) => getBridgeStatus(activePlayback, eventId, nowMs),
    [activePlayback, nowMs],
  );

  const claimLocalFilePlayer = useCallback(async (eventId: string) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const nowIso = new Date().toISOString();
    try {
      await setDoc(doc(db, 'users', uid), {
        localFilePlayer: { deviceId, eventId, claimedAt: nowIso, heartbeatAt: nowIso },
      }, { merge: true });
    } catch (error) {
      console.error('Could not set this device to play local files:', error);
      throw error;
    }
  }, [deviceId]);

  const beatLocalFilePlayer = useCallback((eventId: string) => {
    const uid = auth.currentUser?.uid;
    const current = localFilePlayerRef.current;
    if (!uid || current?.deviceId !== deviceId || current.eventId !== eventId) return;
    updateDoc(doc(db, 'users', uid), { 'localFilePlayer.heartbeatAt': new Date().toISOString() }).catch((error) => {
      console.error('Could not refresh the local file player heartbeat:', error);
    });
  }, [deviceId]);

  const releaseLocalFilePlayer = useCallback(async (eventId: string) => {
    const uid = auth.currentUser?.uid;
    const current = localFilePlayerRef.current;
    if (!uid || current?.deviceId !== deviceId || current.eventId !== eventId) return;
    try {
      await setDoc(doc(db, 'users', uid), { localFilePlayer: null }, { merge: true });
    } catch (error) {
      console.error('Could not release the local file player:', error);
      throw error;
    }
  }, [deviceId]);

  // Send a playback command to the desktop helper via Firestore
  const sendRemoteCommand = useCallback(async (cmd: PlaybackCommand) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, {
      playbackCommand: { action: cmd, timestamp: new Date().toISOString() },
    }, { merge: true });
  }, []);

  // Tell desktop helper to switch to a different event
  const sendSwitchEventCommand = useCallback(async (eventId: string, eventName: string) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, {
      playbackCommand: { action: 'switchEvent', eventId, eventName, timestamp: new Date().toISOString() },
    }, { merge: true });
  }, []);

  const acknowledgePlayerMismatch = useCallback(async (detectedAt: string) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const userRef = doc(db, 'users', uid);
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(userRef);
      const stored = parsePlayerMismatch(snap.data()?.['playerMismatch']);
      if (!stored || stored.detectedAt !== detectedAt || stored.acknowledged) return;
      transaction.update(userRef, { 'playerMismatch.acknowledged': true });
    });
  }, []);

  const clearPlayerMismatch = useCallback(async (eventId: string) => {
    const uid = auth.currentUser?.uid;
    if (!uid || playerMismatch?.eventId !== eventId) return;
    await setDoc(doc(db, 'users', uid), { playerMismatch: null }, { merge: true });
  }, [playerMismatch?.eventId]);

  const publishQueueNextUp = useCallback((eventId: string, requestId: string | null) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const queueNextUp: QueueNextUp = { eventId, requestId, updatedAt: new Date().toISOString() };
    setDoc(doc(db, 'users', uid), { queueNextUp }, { merge: true }).catch((error) => {
      console.error('Could not publish the next queued song for the desktop helper:', error);
    });
  }, []);

  // This tab should poll only if it created the lock
  const isActivePoller = activePlayback?.deviceId === deviceId;

  return (
    <ActivePlaybackContext.Provider value={{ activePlayback, lockPlayback, unlockPlayback, isLockedToOtherEvent, isActivePoller, detectedPlayerName, sendRemoteCommand, sendSwitchEventCommand, playerMismatch, acknowledgePlayerMismatch, clearPlayerMismatch, publishQueueNextUp, deviceId, bridgeStatusFor, localFilePlayer, localFilePlayerLoaded, claimLocalFilePlayer, releaseLocalFilePlayer, beatLocalFilePlayer, bridgeLaunchAck, playerPositionAnchorMs }}>
      {children}
    </ActivePlaybackContext.Provider>
  );
}

export function useActivePlayback() {
  const ctx = useContext(ActivePlaybackContext);
  if (!ctx) throw new Error('useActivePlayback must be used within ActivePlaybackProvider');
  return ctx;
}
